import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Newspaper, Calendar, ArrowRight, Trophy, Sparkles, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import BirdMascot from '../components/BirdMascot';
import Footer from '../components/Footer';
import blogImages from '../data/blogImages';

const API = process.env.REACT_APP_BACKEND_URL;
// County mugshot URLs are http:// and only load via our proxy (mixed-content + hotlink).
// Other absolute image URLs (e.g. hosted hero art) are https and load directly.
const proxied = (u) => {
  if (!u) return u;
  if (/mecklenburgcountync\.gov/i.test(u)) return `${API}/api/mugshots/image-proxy?url=${encodeURIComponent(u)}`;
  return u;
};

const BlogPost = ({ title, excerpt, date, category, slug, featured, image }) => (
  <motion.article
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className={`group bg-card border border-border rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow ${featured ? 'md:col-span-2' : ''}`}
  >
    {image && (
      <Link to={`/blog/${slug}`} className={`block overflow-hidden bg-muted ${featured ? 'aspect-[21/9]' : 'aspect-[16/9]'}`}>
        <img
          src={image}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
    )}
    <div className="p-6">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-medium px-2 py-1 rounded-full bg-primary/20 text-primary">
          {category}
        </span>
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {date}
        </span>
      </div>
      <h2 className={`font-heading font-bold mb-3 ${featured ? 'text-2xl' : 'text-xl'}`}>
        {title}
      </h2>
      <p className="text-muted-foreground mb-4 leading-relaxed">
        {excerpt}
      </p>
      <Link 
        to={`/blog/${slug}`}
        className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
      >
        Read More <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </motion.article>
);

const BlogPage = () => {
  const [dynamicPosts, setDynamicPosts] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/api/blog/posts`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.success) setDynamicPosts(d.posts || []); })
      .catch(() => {});
  }, []);

  const blogPosts = [
    {
      title: "Welcome to JailBirds: A New Way to Test Your Instincts",
      excerpt: "We're excited to launch JailBirds, a unique guessing game that challenges your ability to match mugshots with criminal charges. Using real public records from Mecklenburg County, NC, our game offers a fascinating look at the criminal justice system while providing entertainment.",
      date: "December 15, 2025",
      category: "Announcement",
      slug: "welcome-to-jailbirds",
      featured: true
    },
    {
      title: "How We Keep the Game Fresh with Daily Updates",
      excerpt: "Ever wonder how JailBirds stays interesting? Our automated system pulls fresh arrest records daily from the Mecklenburg County Sheriff's Office, ensuring you never see the same mugshots twice. Here's a behind-the-scenes look at how it works.",
      date: "December 16, 2025",
      category: "Behind the Scenes",
      slug: "daily-updates-explained"
    },
    {
      title: "Top Strategies from Our Highest Scorers",
      excerpt: "We interviewed some of our top leaderboard players to find out their secrets. From reading body language to understanding charge patterns, here are the strategies that can help you climb the rankings.",
      date: "December 17, 2025",
      category: "Tips & Tricks",
      slug: "top-player-strategies"
    },
    {
      title: "Understanding the JailBird Bonus Round",
      excerpt: "The bonus round can make or break your final score. Learn how the time-based multiplier works and discover techniques to maximize your bonus points without sacrificing accuracy.",
      date: "December 18, 2025",
      category: "Game Guide",
      slug: "bonus-round-guide"
    },
    {
      title: "The Ethics of Mugshot Games: Our Approach",
      excerpt: "Using real mugshots for entertainment raises important questions. We explain our ethical guidelines, why we believe in the presumption of innocence, and how we handle the sensitive nature of arrest records.",
      date: "December 18, 2025",
      category: "Editorial",
      slug: "ethics-of-mugshot-games"
    },
    {
      title: "New: Daily Streaks & Achievements Have Arrived",
      excerpt: "JailBirds just got a whole lot more rewarding. Build a daily play streak, unlock 13 collectible achievements from Rookie Detective to Unstoppable, and watch your past stats automatically earn you the badges you've already deserved. Here's everything that's new.",
      date: "June 2, 2026",
      category: "New Feature",
      slug: "daily-streaks-and-achievements",
      featured: true
    },
    {
      title: "React to Every Mugshot: Emoji Reactions Are Live",
      excerpt: "Got a strong opinion on a booking photo? Now you can show it. Drop a 👍, 👎, ❤️, 🔥, 🍆, or 💦 on any mugshot during play — including the JailBird Bonus round — and see how the rest of the community reacted.",
      date: "June 5, 2026",
      category: "New Feature",
      slug: "emoji-reactions-launch"
    },
    {
      title: "Introducing the 10 Most Reacted Mugshots",
      excerpt: "The people have spoken — and we're showing off their picks. The new '10 Most Reacted Mugshots' gallery surfaces the booking photos players can't stop reacting to, now featured on the home page, leaderboard, and community stats.",
      date: "June 8, 2026",
      category: "New Feature",
      slug: "most-reacted-mugshots-gallery"
    },
    {
      title: "Share the Laughs with Hall of Shame Cards",
      excerpt: "Tap any of the most-reacted mugshots and you'll unlock a slick, shareable 'Hall of Shame' card — perfect for posting straight to our Facebook group. Download the image or fire it off to Facebook and X in one click.",
      date: "June 10, 2026",
      category: "Community",
      slug: "hall-of-shame-shareable-cards"
    },
    {
      title: "Dive Into the New Community Stats Page",
      excerpt: "Curious how you stack up against everyone? Our Community Stats page packs interactive charts, a Hall of Fame, and a fully searchable global leaderboard with a 'Jump to my rank' button. Find out where you really stand.",
      date: "June 12, 2026",
      category: "New Feature",
      slug: "community-stats-deep-dive"
    },
    {
      title: "Gold-Tier Wins Now Get a Confetti Celebration",
      excerpt: "Nail a perfect game or hit a rare milestone and JailBirds throws you a (tasteful) gold confetti party — right when the Game Complete screen reveals your shiny new badge. The perfect moment for a victory screenshot.",
      date: "June 15, 2026",
      category: "New Feature",
      slug: "gold-tier-celebrations"
    },
    {
      title: "Fresher Than Ever: Inside Our Daily Mugshot Pipeline",
      excerpt: "We rebuilt the engine that keeps JailBirds stocked with brand-new faces every single day, squashing the gremlins that occasionally caused empty days. The result: more reliable daily drops of fresh, playable arrest records.",
      date: "June 18, 2026",
      category: "Behind the Scenes",
      slug: "daily-pipeline-upgrade"
    },
    {
      title: "How to Climb the Global Leaderboard in 2026",
      excerpt: "With streaks, achievements, and bonus rounds all in play, the path to the top has never had more layers. Here's an updated game plan for racking up points, staying consistent, and overtaking the regulars.",
      date: "June 20, 2026",
      category: "Tips & Tricks",
      slug: "climbing-the-global-leaderboard"
    },
    {
      title: "Join the JailBirds Facebook Community",
      excerpt: "The best mugshot reactions, score brags, and daily trash talk happen in our Facebook group. Tap the new in-app button to join thousands of fellow detectives sharing their funniest finds and biggest wins.",
      date: "June 22, 2026",
      category: "Community",
      slug: "join-the-facebook-community"
    },
    {
      title: "Advertise With JailBirds: Reach Thousands of Engaged Players",
      excerpt: "JailBirds isn't just a game — it's a fast-growing audience of daily, attention-rich players. With our in-game video ad placements and a dedicated advertising program, your brand can land in front of exactly the right eyeballs. Here's how to get started.",
      date: "June 24, 2026",
      category: "Advertising",
      slug: "advertise-with-jailbirds",
      featured: true
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-muted/30 to-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <BirdMascot size={32} />
            <span className="text-xl font-heading font-bold">JailBirds</span>
          </Link>
          
          <Link to="/">
            <Button variant="outline" size="sm" className="gap-2">
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-8"
        >
          {/* Hero Section */}
          <div className="text-center mb-8">
            <Newspaper className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4 text-gradient-primary">
              JailBirds Blog
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              News, updates, tips, and insights from the JailBirds team
            </p>
          </div>

          {/* Featured Categories */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {['All', 'Announcement', 'Tips & Tricks', 'Game Guide', 'Behind the Scenes'].map((cat) => (
              <Button
                key={cat}
                variant={cat === 'All' ? 'default' : 'outline'}
                size="sm"
                className="rounded-full"
              >
                {cat}
              </Button>
            ))}
          </div>

          {/* Blog Posts Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {[...dynamicPosts, ...blogPosts]
              .sort((a, b) => new Date(b.date) - new Date(a.date))
              .map((post) => (
                <BlogPost key={post.slug} {...post} image={post.image ? proxied(post.image) : blogImages[post.slug]} />
              ))}
          </div>

          {/* Newsletter Signup */}
          <div className="bg-card border border-border rounded-lg p-8 text-center">
            <Sparkles className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold mb-2">Stay Updated</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Follow us for game updates, new features, and tips to improve your score!
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <Button>Subscribe</Button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <Trophy className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold">1000+</p>
              <p className="text-sm text-muted-foreground">Games Played</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <TrendingUp className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold">Daily</p>
              <p className="text-sm text-muted-foreground">Fresh Content</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <BirdMascot size={32} className="mx-auto mb-2" />
              <p className="text-2xl font-bold">Free</p>
              <p className="text-sm text-muted-foreground">To Play</p>
            </div>
          </div>

        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogPage;
